import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  DataTable,
  DataTableColumnSettings,
  DataTableProvider,
  type TableColumnDef,
} from "../../../components/data-table";
import { Button } from "../../../components/ui/button";
import { Plus, Edit, Trash2, Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Checkbox } from "../../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../components/ui/tabs";
import { toast } from "sonner";
import {
  galleryAPI,
  galleryCategoriesAPI,
  type GalleryCategory,
  type GalleryItem,
} from "../../../services/api";
import { CommonPagination } from "../../../components/CommonPagination";

const initialCategoryForm = {
  name: "",
  display_order: 0,
  is_enabled: true,
};

const initialGalleryForm = {
  category: "",
  title: "",
  description: "",
  year: new Date().getFullYear().toString(),
  display_order: 0,
  image: null as File | null,
};

export default function MediaGallery() {
  const [categories, setCategories] = useState<GalleryCategory[]>([]);
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Category State
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<
    string | number | null
  >(null);
  const [deletingCategoryId, setDeletingCategoryId] = useState<
    string | number | null
  >(null);
  const [categoryForm, setCategoryForm] = useState(initialCategoryForm);
  const [savingCategory, setSavingCategory] = useState(false);

  // Gallery Item State
  const [galleryModalOpen, setGalleryModalOpen] = useState(false);
  const [editingGalleryId, setEditingGalleryId] = useState<
    string | number | null
  >(null);
  const [deletingGalleryId, setDeletingGalleryId] = useState<
    string | number | null
  >(null);
  const [galleryForm, setGalleryForm] = useState(initialGalleryForm);
  const [savingGallery, setSavingGallery] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Pagination states
  const [catCurrentPage, setCatCurrentPage] = useState(1);
  const [catPageSize, setCatPageSize] = useState(10);

  const [galCurrentPage, setGalCurrentPage] = useState(1);
  const [galPageSize, setGalPageSize] = useState(10);

  const catTotalItems = categories.length;
  const catTotalPages = Math.ceil(catTotalItems / catPageSize);
  const paginatedCategories = categories.slice(
    (catCurrentPage - 1) * catPageSize,
    catCurrentPage * catPageSize
  );

  const galTotalItems = galleryItems.length;
  const galTotalPages = Math.ceil(galTotalItems / galPageSize);
  const paginatedGalleryItems = galleryItems.slice(
    (galCurrentPage - 1) * galPageSize,
    galCurrentPage * galPageSize
  );

  const loadData = async () => {
    setLoading(true);
    try {
      const [catData, galData] = await Promise.all([
        galleryCategoriesAPI.list(),
        galleryAPI.list(),
      ]);
      setCategories(Array.isArray(catData) ? catData : []);
      setGalleryItems(Array.isArray(galData) ? galData : []);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // --- Category Handlers ---
  const openAddCategory = () => {
    setCategoryForm(initialCategoryForm);
    setEditingCategoryId(null);
    setCategoryModalOpen(true);
  };

  const openEditCategory = (cat: GalleryCategory) => {
    setCategoryForm({
      name: cat.name,
      display_order: cat.display_order ?? 0,
      is_enabled: cat.is_enabled ?? true,
    });
    setEditingCategoryId(cat.id);
    setCategoryModalOpen(true);
  };

  const saveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error("Please enter a category name");
      return;
    }
    setSavingCategory(true);
    try {
      if (editingCategoryId) {
        await galleryCategoriesAPI.update(editingCategoryId, categoryForm);
        toast.success("Category updated");
      } else {
        await galleryCategoriesAPI.create(categoryForm);
        toast.success("Category created");
      }
      setCategoryModalOpen(false);
      loadData();
    } catch {
      toast.error("Failed to save category");
    } finally {
      setSavingCategory(false);
    }
  };

  const deleteCategory = async () => {
    if (!deletingCategoryId) {
      return;
    }
    try {
      await galleryCategoriesAPI.delete(deletingCategoryId);
      toast.success("Category deleted");
      loadData();
    } catch {
      toast.error("Failed to delete category");
    } finally {
      setDeletingCategoryId(null);
    }
  };

  // --- Gallery Item Handlers ---
  const openAddGallery = () => {
    setGalleryForm(initialGalleryForm);
    setImagePreview(null);
    setEditingGalleryId(null);
    setGalleryModalOpen(true);
  };

  const openEditGallery = (item: GalleryItem) => {
    setGalleryForm({
      category: String(item.category),
      title: item.title ?? "",
      description: item.description ?? "",
      year: item.year ? String(item.year) : "",
      display_order: item.display_order ?? 0,
      image: null,
    });
    setImagePreview(item.image ?? null);
    setEditingGalleryId(item.id);
    setGalleryModalOpen(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setGalleryForm(prev => ({ ...prev, image: file }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const saveGallery = async () => {
    if (!galleryForm.category) {
      toast.error("Please select a category");
      return;
    }
    if (!galleryForm.title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    setSavingGallery(true);
    try {
      const formData = new FormData();
      formData.append("category", String(galleryForm.category));
      formData.append("title", galleryForm.title);
      formData.append("description", galleryForm.description || "");
      formData.append("year", galleryForm.year || "");
      formData.append("display_order", String(galleryForm.display_order));
      if (galleryForm.image) {
        formData.append("image", galleryForm.image);
      }

      if (editingGalleryId) {
        await galleryAPI.update(editingGalleryId, formData);
        toast.success("Gallery item updated");
      } else {
        await galleryAPI.create(formData);
        toast.success("Gallery item created");
      }
      setGalleryModalOpen(false);
      loadData();
    } catch {
      toast.error("Failed to save gallery item");
    } finally {
      setSavingGallery(false);
    }
  };

  const deleteGallery = async () => {
    if (!deletingGalleryId) {
      return;
    }
    try {
      await galleryAPI.delete(deletingGalleryId);
      toast.success("Gallery item deleted");
      loadData();
    } catch {
      toast.error("Failed to delete gallery item");
    } finally {
      setDeletingGalleryId(null);
    }
  };

  const categoryTableColumns = useMemo<TableColumnDef<GalleryCategory>[]>(
    () => [
      {
        id: "name",
        label: "Name",
        width: 180,
        renderCell: cat => <span className="font-medium">{cat.name}</span>,
      },
      {
        id: "displayOrder",
        label: "Display Order",
        width: 120,
        renderCell: cat => cat.display_order,
      },
      {
        id: "enabled",
        label: "Enabled",
        width: 80,
        renderCell: cat => (cat.is_enabled ? "Yes" : "No"),
      },
      {
        id: "actions",
        label: "Actions",
        width: 100,
        alwaysVisible: true,
        headerClassName: "text-right",
        cellClassName: "text-right",
        renderCell: cat => (
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => openEditCategory(cat)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDeletingCategoryId(cat.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold text-foreground">Media Gallery</h1>
        <p className="text-muted-foreground">
          Manage your gallery categories and images.
        </p>
      </div>

      <Tabs defaultValue="gallery" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="gallery">Gallery Items</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
        </TabsList>

        {/* Gallery Items Tab */}
        <TabsContent value="gallery" className="space-y-4">
          <div className="flex justify-end">
            <Button className="gap-2" onClick={openAddGallery}>
              <Plus className="h-4 w-4" />
              Add Gallery Item
            </Button>
          </div>
          <div className="mt-4">
            {loading ? (
              <div className="flex h-32 items-center justify-center rounded-lg border bg-card">
                <CustomLoader size="sm" />
              </div>
            ) : galleryItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border bg-card py-12 text-card-foreground shadow-sm">
                <p className="text-muted-foreground">No gallery items found.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {paginatedGalleryItems.map(item => (
                  <Card
                    key={item.id}
                    className="flex flex-col overflow-hidden rounded-xl border shadow-sm"
                  >
                    {/* Image section */}
                    <div className="relative aspect-video w-full shrink-0 overflow-hidden bg-muted">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.title}
                          className="absolute inset-0 h-full w-full object-cover"
                        />
                      ) : (
                        <div className="absolute inset-0 flex h-full w-full items-center justify-center text-sm text-muted-foreground">
                          No Image
                        </div>
                      )}
                    </div>
                    {/* Content section */}
                    <CardContent className="flex flex-grow flex-col p-4">
                      <h3 className="mb-3 line-clamp-1 text-base font-bold uppercase tracking-wide text-foreground">
                        {item.title}
                      </h3>
                      <div className="mb-6 flex items-center gap-2">
                        <span className="inline-flex items-center rounded bg-secondary/50 px-2 py-1 text-xs font-medium text-foreground">
                          {item.year || "N/A"}
                        </span>
                        <span className="inline-flex items-center rounded bg-secondary/50 px-2 py-1 text-xs font-medium text-foreground">
                          {item.category_name ??
                            categories.find(
                              c => String(c.id) === String(item.category)
                            )?.name ??
                            item.category ??
                            "General"}
                        </span>
                      </div>
                      {/* Action buttons */}
                      <div className="mt-auto flex items-center gap-2">
                        <Button
                          variant="outline"
                          className="flex-1 gap-2 rounded-lg font-medium text-foreground"
                          onClick={() => openEditGallery(item)}
                        >
                          <Edit className="h-4 w-4" /> Edit
                        </Button>
                        <Button
                          variant="outline"
                          className="shrink-0 rounded-lg px-3 text-red-500 hover:bg-red-50 hover:text-red-600"
                          onClick={() => setDeletingGalleryId(item.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            {!loading && galTotalItems > 0 && (
              <div className="mt-4 border-t pt-4">
                <CommonPagination
                  currentPage={galCurrentPage}
                  totalPages={galTotalPages}
                  pageSize={galPageSize}
                  totalCount={galTotalItems}
                  onPageChange={setGalCurrentPage}
                  onPageSizeChange={size => {
                    setGalPageSize(size);
                    setGalCurrentPage(1);
                  }}
                />
              </div>
            )}
          </div>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <DataTableProvider
            tableId="super-admin-cms-media-gallery-categories"
            columns={categoryTableColumns}
          >
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
                <CardTitle>Categories ({catTotalItems})</CardTitle>
                <div className="flex items-center gap-2">
                  <Button className="gap-2" onClick={openAddCategory}>
                    <Plus className="h-4 w-4" />
                    Add Category
                  </Button>
                  <DataTableColumnSettings columns={categoryTableColumns} />
                </div>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                {loading ? (
                  <div className="flex h-32 items-center justify-center">
                    <CustomLoader size="sm" />
                  </div>
                ) : (
                  <DataTable
                    embedded
                    hideToolbar
                    tableId="super-admin-cms-media-gallery-categories"
                    columns={categoryTableColumns}
                    data={paginatedCategories}
                    getRowKey={cat => cat.id}
                    emptyMessage="No categories found."
                  />
                )}
                {!loading && catTotalItems > 0 && (
                  <div className="border-t p-4">
                    <CommonPagination
                      currentPage={catCurrentPage}
                      totalPages={catTotalPages}
                      pageSize={catPageSize}
                      totalCount={catTotalItems}
                      onPageChange={setCatCurrentPage}
                      onPageSizeChange={size => {
                        setCatPageSize(size);
                        setCatCurrentPage(1);
                      }}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </DataTableProvider>
        </TabsContent>
      </Tabs>

      {/* Category Modal */}
      <Dialog open={categoryModalOpen} onOpenChange={setCategoryModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategoryId ? "Edit Category" : "Add Category"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category Name</Label>
              <Input
                value={categoryForm.name}
                onChange={e =>
                  setCategoryForm({ ...categoryForm, name: e.target.value })
                }
                placeholder="e.g. Cricket"
              />
            </div>
            <div className="space-y-2">
              <Label>Display Order</Label>
              <Input
                type="number"
                value={categoryForm.display_order}
                onChange={e =>
                  setCategoryForm({
                    ...categoryForm,
                    display_order: parseInt(e.target.value) || 0,
                  })
                }
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="cat-enabled"
                checked={categoryForm.is_enabled}
                onCheckedChange={c =>
                  setCategoryForm({ ...categoryForm, is_enabled: c === true })
                }
              />
              <Label htmlFor="cat-enabled">Enabled</Label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setCategoryModalOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={saveCategory} disabled={savingCategory}>
              {savingCategory && <CustomLoader size="xs" className="mr-2" />}
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Gallery Modal */}
      <Dialog open={galleryModalOpen} onOpenChange={setGalleryModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingGalleryId ? "Edit Gallery Item" : "Add Gallery Item"}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[70vh] space-y-4 overflow-y-auto px-2 py-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={galleryForm.category}
                onValueChange={val =>
                  setGalleryForm({ ...galleryForm, category: val })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Category" />
                </SelectTrigger>
                <SelectContent>
                  {categories
                    .filter(c => c.is_enabled)
                    .map(cat => (
                      <SelectItem key={cat.id} value={String(cat.id)}>
                        {cat.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={galleryForm.title}
                onChange={e =>
                  setGalleryForm({ ...galleryForm, title: e.target.value })
                }
                placeholder="e.g. Annual Event 2024"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={galleryForm.description}
                onChange={e =>
                  setGalleryForm({
                    ...galleryForm,
                    description: e.target.value,
                  })
                }
                placeholder="Description of the event..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Year</Label>
                <Input
                  value={galleryForm.year}
                  onChange={e =>
                    setGalleryForm({ ...galleryForm, year: e.target.value })
                  }
                  placeholder="2024"
                />
              </div>
              <div className="space-y-2">
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={galleryForm.display_order}
                  onChange={e =>
                    setGalleryForm({
                      ...galleryForm,
                      display_order: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Image</Label>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 rounded-lg border border-dashed bg-muted/30 p-4">
                  <label className="flex flex-1 cursor-pointer items-center gap-2 text-muted-foreground hover:text-foreground">
                    <Upload className="h-4 w-4" />
                    <span className="text-sm">
                      {galleryForm.image
                        ? galleryForm.image.name
                        : imagePreview
                          ? "Change Image"
                          : "Upload Image"}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageChange}
                    />
                  </label>
                </div>
                {imagePreview && (
                  <div className="relative h-40 w-full overflow-hidden rounded-lg border bg-muted">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setGalleryModalOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={saveGallery} disabled={savingGallery}>
              {savingGallery && <CustomLoader size="xs" className="mr-2" />}
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deletingCategoryId}
        onOpenChange={o => !o && setDeletingCategoryId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this category.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteCategory}
              className="text-destructive-foreground bg-destructive"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!deletingGalleryId}
        onOpenChange={o => !o && setDeletingGalleryId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Gallery Item?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this image from the gallery.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteGallery}
              className="text-destructive-foreground bg-destructive"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
